import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from '../components/Toast';
import { authHeaders } from '../utils/api';
import { buildMediaBundle, escapeHtml, isPreviewableMime } from '../utils/media';
import { compressImage } from '../utils/imageCompress';

const MAX_RETRIES = 2;
const CONCURRENCY = 3;

const errorFromResponse = (xhr) => {
    try {
        const body = JSON.parse(xhr.responseText);
        if (body.error) return body.error;
    } catch {}
    if (xhr.status === 507) return '서버 저장 공간이 부족합니다. (10GB 한도 초과)';
    return `업로드 실패: ${xhr.status || ''} ${xhr.statusText || ''}`.trim();
};

const abortError = () => Object.assign(new Error('AbortError'), { name: 'AbortError' });

/**
 * 파일 업로드: 3개씩 동시에 올리고(순서는 유지), 취소·실패한 파일만 다시 올리기를 지원한다.
 * 사진은 기본적으로 크기를 줄여서 올린다 (원본 화질 선택 가능).
 * 완료되면 사진/영상은 미디어 묶음 메시지로, 나머지는 다운로드 링크 메시지로 전송한다.
 *
 * progress: { name, percentage, stage: 'preparing' | 'uploading' | 'failed', failedCount, errors }
 */
export function useFileUpload(sendMessage, channelId) {
    const [progress, setProgress] = useState(null);
    const jobRef = useRef(null);
    const retryRef = useRef(null); // 실패한 파일 목록 (다시 시도용)
    // 서버가 파일을 채널 폴더에 바로 저장하고 권한을 걸 수 있도록 채널을 같이 보낸다.
    // ref 로 들고 있어야 채널을 옮겨도 uploadOne 의 정체성이 바뀌지 않는다.
    const channelRef = useRef(channelId);
    useEffect(() => { channelRef.current = channelId; }, [channelId]);

    const uploadOne = useCallback((file, job, onProgress, attempt = 0) => new Promise((resolve, reject) => {
        if (job.cancelled) return reject(abortError());
        const form = new FormData();
        // channelId 를 files 보다 **먼저** 넣어야 한다 — 서버(multer)는 폼 필드를 만난 순서대로
        // 채우므로, 뒤에 붙이면 저장 위치를 정할 때 아직 못 읽는다.
        if (channelRef.current != null) form.append('channelId', String(channelRef.current));
        form.append('files', file);
        const xhr = new XMLHttpRequest();
        // 취소는 요청 상태와 상관없이 즉시 끝나야 한다 (전송 시작 전 abort 는 이벤트가 발생하지 않음)
        const cancel = () => { xhr.abort(); reject(abortError()); };
        job.cancels.add(cancel);
        const done = () => job.cancels.delete(cancel);
        xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded); };
        xhr.onload = () => {
            done();
            if (xhr.status < 200 || xhr.status >= 300) return reject(new Error(errorFromResponse(xhr)));
            try { resolve(JSON.parse(xhr.responseText).files || []); }
            catch { reject(new Error('응답 파싱 실패')); }
        };
        xhr.onerror = () => {
            done();
            if (job.cancelled) return reject(abortError());
            if (attempt >= MAX_RETRIES) return reject(new Error('네트워크 오류가 발생했습니다.'));
            onProgress(0);
            setTimeout(() => uploadOne(file, job, onProgress, attempt + 1).then(resolve, reject), 1000 * (attempt + 1));
        };
        xhr.onabort = () => { done(); reject(abortError()); };
        xhr.open('POST', '/api/uploads');
        Object.entries(authHeaders()).forEach(([k, v]) => xhr.setRequestHeader(k, v));
        xhr.send(form);
    }), []);

    const uploadFiles = useCallback(async (input, { original = false } = {}) => {
        const sourceFiles = Array.from(input || []);
        if (!sourceFiles.length) return;
        if (jobRef.current) { toast.info('진행 중인 업로드가 끝난 뒤 다시 시도해 주세요.'); return; }
        const job = { cancelled: false, cancels: new Set(), retryFiles: [], original };
        jobRef.current = job;
        const name = sourceFiles.length > 1 ? `${sourceFiles.length}개 파일` : sourceFiles[0].name;

        try {
            // 1) 사진 크기 줄이기
            let files = sourceFiles;
            if (!original && sourceFiles.some(f => /^image\/(jpeg|png|webp)$/.test(f.type))) {
                setProgress({ name, percentage: 0, stage: 'preparing' });
                files = [];
                for (const f of sourceFiles) {
                    if (job.cancelled) throw abortError();
                    files.push(await compressImage(f));
                }
            }

            // 2) 동시 업로드 (완료 순서와 상관없이 원래 순서대로 결과를 모은다)
            const totalBytes = files.reduce((sum, f) => sum + (f.size || 0), 0) || 1;
            const loaded = new Array(files.length).fill(0);
            const report = () => setProgress({
                name, stage: 'uploading',
                percentage: Math.min(99, Math.round(loaded.reduce((a, b) => a + b, 0) / totalBytes * 100)),
            });
            report();
            const results = new Array(files.length);
            let next = 0;
            const worker = async () => {
                while (next < files.length && !job.cancelled) {
                    const index = next++;
                    try {
                        results[index] = { ok: true, files: await uploadOne(files[index], job, (bytes) => { loaded[index] = bytes; report(); }) };
                        loaded[index] = files[index].size || 0;
                        report();
                    } catch (err) {
                        results[index] = { ok: false, error: err };
                    }
                }
            };
            await Promise.all(Array.from({ length: Math.min(CONCURRENCY, files.length) }, worker));
            if (job.cancelled) throw abortError();

            // 3) 성공한 파일만 메시지로 전송
            const uploaded = results.filter(r => r?.ok).flatMap(r => r.files);
            const media = uploaded.filter(f => isPreviewableMime(f.mimetype));
            const others = uploaded.filter(f => !isPreviewableMime(f.mimetype));
            if (media.length) sendMessage(buildMediaBundle(media));
            if (others.length) {
                sendMessage(others.map(f =>
                    `<div><a href="${escapeHtml(f.downloadUrl || f.fileUrl)}" target="_blank" class="file-link">📎 ${escapeHtml(f.originalName)}</a></div>`
                ).join(''));
            }

            const failed = results.map((r, i) => (r?.ok ? null : { file: sourceFiles[i], error: r?.error })).filter(Boolean);
            if (failed.length) {
                job.retryFiles = failed.map(f => f.file);
                const reasons = [...new Set(failed.map(f => f.error?.message).filter(Boolean))];
                setProgress({ name, percentage: 100, stage: 'failed', failedCount: failed.length, total: files.length, errors: reasons });
                return;
            }
            setProgress({ name, percentage: 100, stage: 'uploading' });
            setTimeout(() => setProgress(p => (p?.stage === 'uploading' ? null : p)), 800);
        } catch (err) {
            if (err.name === 'AbortError') {
                setProgress(null);
                toast.info('업로드를 취소했습니다.');
            } else {
                setProgress(null);
                toast.error(err.message || '업로드 중 오류가 발생했습니다.');
            }
        } finally {
            if (jobRef.current === job) {
                // 실패 목록은 "다시 시도"를 위해 남겨두되, 새 업로드는 받을 수 있게 한다
                jobRef.current = null;
                retryRef.current = job.retryFiles.length ? { files: job.retryFiles, original: job.original } : null;
            }
        }
    }, [sendMessage, uploadOne]);

    const cancelUpload = useCallback(() => {
        const job = jobRef.current;
        if (!job) return;
        job.cancelled = true;
        [...job.cancels].forEach(cancel => cancel());
    }, []);

    const retryFailed = useCallback(() => {
        const retry = retryRef.current;
        retryRef.current = null;
        setProgress(null);
        if (retry?.files.length) uploadFiles(retry.files, { original: retry.original });
    }, [uploadFiles]);

    const dismissProgress = useCallback(() => { retryRef.current = null; setProgress(null); }, []);

    return { uploadFiles, uploadProgress: progress, cancelUpload, retryFailed, dismissProgress };
}
